import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import {
    formatTimestampForMerchantUi,
    TerminalCommandReadinessRequestOperation,
    TerminalCommandReadinessResourceKind,
    TerminalCommandReadinessStatus,
    TerminalCommandReadinessVersion,
    type TerminalCommandReadinessChecklistPageRequestV1,
    type TerminalCommandReadinessResultV1,
} from '@pillar-portal/api-types';
import { useInfiniteScrollList } from '@/hooks/useInfiniteScrollList';
import { paymentsInfrastructureApi } from '@/lib/pillarApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TerminalCommandReadinessResultView } from './TerminalCommandReadinessAction';

const TERMINAL_COMMAND_READINESS_PAGE_SIZE = 50;

export interface TerminalCommandReadinessFleetVerdict {
    readonly resourceKind: TerminalCommandReadinessResourceKind;
    readonly status: 'pass' | 'error';
    readonly resultCount: number;
    readonly nonPassCount: number;
}

export interface TerminalCommandReadinessFleetProps {
    readonly organizationUuid: string;
    readonly onVerdictChange: (
        resourceKind: TerminalCommandReadinessResourceKind,
        verdict: TerminalCommandReadinessFleetVerdict | null
    ) => void;
}

export function TerminalCommandReadinessFleet({
    organizationUuid,
    onVerdictChange,
}: TerminalCommandReadinessFleetProps): ReactElement {
    return (
        <Card data-testid="terminal-command-readiness-fleet">
            <CardHeader>
                <CardTitle>Terminal command readiness</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 lg:grid-cols-2">
                <TerminalCommandReadinessFleetList
                    organizationUuid={organizationUuid}
                    resourceKind={TerminalCommandReadinessResourceKind.LOGICAL_TERMINAL}
                    onVerdictChange={onVerdictChange}
                />
                <TerminalCommandReadinessFleetList
                    organizationUuid={organizationUuid}
                    resourceKind={TerminalCommandReadinessResourceKind.TERMINAL_DEVICE}
                    onVerdictChange={onVerdictChange}
                />
            </CardContent>
        </Card>
    );
}

function TerminalCommandReadinessFleetList({
    organizationUuid,
    resourceKind,
    onVerdictChange,
}: TerminalCommandReadinessFleetProps & {
    readonly resourceKind: TerminalCommandReadinessResourceKind;
}): ReactElement {
    const [runRevision, setRunRevision] = useState(0);
    const resourceLabel =
        resourceKind === TerminalCommandReadinessResourceKind.LOGICAL_TERMINAL
            ? 'Logical terminals'
            : 'Terminal devices';
    const resourceDescription =
        resourceKind === TerminalCommandReadinessResourceKind.LOGICAL_TERMINAL
            ? 'configured logical terminal'
            : 'configured terminal device';
    const queryKeyPrefix = useMemo(
        () =>
            [
                'merchant-checklist',
                organizationUuid,
                'terminal-command-readiness',
                resourceKind,
                runRevision,
            ] as const,
        [organizationUuid, resourceKind, runRevision]
    );
    const fetchPage = useCallback(
        async ({ cursor, limit }: { readonly cursor?: string; readonly limit: number }) => {
            const request: TerminalCommandReadinessChecklistPageRequestV1 = {
                terminal_command_readiness_version: TerminalCommandReadinessVersion,
                operation: TerminalCommandReadinessRequestOperation.CHECKLIST_PAGE,
                organization_uuid: organizationUuid,
                resource_kind: resourceKind,
                limit,
                ...(cursor === undefined ? {} : { cursor }),
            };
            const page =
                await paymentsInfrastructureApi.checkMerchantChecklistTerminalCommandReadinessPage(
                    request
                );
            return { items: page.results, pagination: page.pagination };
        },
        [organizationUuid, resourceKind]
    );
    const list = useInfiniteScrollList<
        TerminalCommandReadinessResultV1,
        string,
        typeof queryKeyPrefix
    >({
        queryKeyPrefix,
        pageSize: TERMINAL_COMMAND_READINESS_PAGE_SIZE,
        fetchPage,
        parseCursorParam: (pageParam) =>
            z
                .string()
                .optional()
                .parse(pageParam ?? undefined),
        enabled: runRevision > 0,
        retry: false,
    });

    useEffect(() => {
        if (
            runRevision === 0 ||
            list.pagination === null ||
            list.pagination.has_more ||
            list.isFetching
        ) {
            return;
        }
        const nonPassCount = list.items.filter(
            (result) =>
                result.status !== TerminalCommandReadinessStatus.COMMAND_ACCEPTED &&
                result.status !== TerminalCommandReadinessStatus.PROVIDER_REPORTED_ONLINE
        ).length;
        onVerdictChange(resourceKind, {
            resourceKind,
            status: nonPassCount === 0 ? 'pass' : 'error',
            resultCount: list.items.length,
            nonPassCount,
        });
    }, [list.isFetching, list.items, list.pagination, onVerdictChange, resourceKind, runRevision]);

    return (
        <section className="space-y-3" aria-label={`${resourceLabel} command readiness`}>
            <div className="flex items-center justify-between gap-3">
                <h3 className="font-medium">{resourceLabel}</h3>
                <Button
                    type="button"
                    variant="outline"
                    disabled={list.isFetching}
                    aria-label={`${runRevision === 0 ? 'Test' : 'Refresh'} ${resourceLabel.toLowerCase()} connections`}
                    onClick={() => {
                        onVerdictChange(resourceKind, null);
                        setRunRevision((currentRunRevision) => currentRunRevision + 1);
                    }}
                >
                    {list.isFetching
                        ? 'Testing...'
                        : runRevision === 0
                          ? 'Test terminal connections'
                          : 'Refresh'}
                </Button>
            </div>
            {runRevision === 0 ? (
                <p className="text-sm text-muted-foreground">
                    Run this explicit check to test every {resourceDescription}.
                </p>
            ) : list.error !== null ? (
                <p className="text-sm text-destructive" role="alert">
                    Unable to test {resourceLabel.toLowerCase()}. Select Refresh to retry.
                </p>
            ) : (
                <>
                    <div className="space-y-3">
                        {list.items.map((result) => (
                            <article
                                key={result.resource.resource_uuid}
                                className="space-y-2 rounded border p-3"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <p className="font-medium">{result.resource.display_name}</p>
                                    <Button asChild type="button" variant="outline" size="sm">
                                        <Link
                                            to={buildTerminalCommandReadinessResourceHref(result)}
                                        >
                                            View
                                        </Link>
                                    </Button>
                                </div>
                                <TerminalCommandReadinessResultView result={result} />
                                <p className="text-xs text-muted-foreground">
                                    Observed{' '}
                                    {formatTimestampForMerchantUi(
                                        result.observed_at,
                                        result.merchant_timezone
                                    )}
                                </p>
                            </article>
                        ))}
                    </div>
                    {list.isLoading ? (
                        <p className="text-sm text-muted-foreground">Testing...</p>
                    ) : list.hasNextPage ? (
                        <p className="text-sm text-muted-foreground">
                            Scroll to load remaining results.
                        </p>
                    ) : list.pagination !== null ? (
                        <p className="text-sm" role="status">
                            {formatFleetVerdict(list.items)}
                        </p>
                    ) : null}
                    <div ref={list.loadMoreSentinelRef} aria-hidden="true" />
                </>
            )}
        </section>
    );
}

function buildTerminalCommandReadinessResourceHref(
    result: TerminalCommandReadinessResultV1
): string {
    switch (result.resource.resource_kind) {
        case TerminalCommandReadinessResourceKind.LOGICAL_TERMINAL:
            return `/payments-infrastructure/logical-terminals/${result.resource.resource_uuid}`;
        case TerminalCommandReadinessResourceKind.TERMINAL_DEVICE:
            return `/payments-infrastructure/terminal-devices/${result.resource.resource_uuid}`;
    }
}

function formatFleetVerdict(results: ReadonlyArray<TerminalCommandReadinessResultV1>): string {
    const nonPassCount = results.filter(
        (result) =>
            result.status !== TerminalCommandReadinessStatus.COMMAND_ACCEPTED &&
            result.status !== TerminalCommandReadinessStatus.PROVIDER_REPORTED_ONLINE
    ).length;
    if (nonPassCount === 0) {
        return `All ${results.length} terminal connections passed.`;
    }
    return `${nonPassCount} of ${results.length} terminal connections did not pass.`;
}
